import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';

// 英文翻译内容
const TRANSLATIONS = {
  zh: {
    sectionTitle: '构建下一代数字文明',
    sectionSubtitle: 'META66.cn 是中国领先的元宇宙垂直综合资讯与生态平台',
    sectionDesc: '致力于汇聚元宇宙产业链核心力量，推动6G网络、Web3.0-Web5.0协议、XR沉浸技术与数字经济的深度融合与创新发展。',
    learnMore: '了解META66使命愿景与发展历程',
    conceptTitle: '元宇宙底层概念深度解析',
    conceptSubtitle: '理解元宇宙的六大维度',
    conceptDesc: '从本质定义到哲学维度，从技术底层到治理体系——全方位解构元宇宙的底层逻辑',
    futureVision: '技术交汇未来愿景 · 10/20/30年演进',
    convergenceTitle: '当所有技术交汇融合',
    convergenceDesc: '6G、AI、量子计算、脑机接口、数字孪生——当这些技术在未来10、20、30年逐步交汇，人类的生活与生产方式将发生怎样的全方位变化？',
    qjTitle: '清鉴元衡',
    qjSubtitle: '科技公司首席运营官 · 前沿科技爱好者 · 跨界创业者',
    coreValues: [
      { title: '前瞻视野', desc: '持续追踪全球元宇宙、6G、Web4.0-5.0最前沿动态，为行业提供最具价值的洞察' },
      { title: '生态连接', desc: '汇聚产业链上下游500+核心机构，打造元宇宙领域最具影响力的生态网络' },
      { title: '价值投资', desc: '深度分析元宇宙赛道投融资机会，为投资者提供专业、可靠的决策参考' },
      { title: '创新驱动', desc: '支持元宇宙创业者与开发者，加速下一代数字文明基础设施的落地与普及' },
    ],
    milestones: [
      { year: '2021', event: 'META66.cn 正式创立，聚焦元宇宙垂直资讯' },
      { year: '2022', event: '生态伙伴突破100家，覆盖VR/AR/区块链全产业链' },
      { year: '2023', event: '推出元宇宙产业导航平台，用户突破500万' },
      { year: '2024', event: '6G技术研究院成立，Web4.0协议联盟发起' },
      { year: '2025', event: '全球节点网络部署完成，数字经济生态全面启动' },
      { year: '2026+', event: '迈向Web5.0时代，构建数字文明新基础设施' },
    ],
  },
  en: {
    sectionTitle: 'Building the Next Digital Civilization',
    sectionSubtitle: 'META66.cn — China\'s premier Metaverse intelligence and ecosystem platform',
    sectionDesc: 'Uniting the core forces of the Metaverse industry chain to drive deep convergence and breakthrough innovation across 6G networks, Web3.0–Web5.0 protocols, XR immersive technology, and the digital economy.',
    learnMore: 'Explore META66\'s Mission, Vision & Milestones',
    conceptTitle: 'Metaverse Fundamentals: A Deep Dive',
    conceptSubtitle: 'Nine Dimensions of the Metaverse',
    conceptDesc: 'From first principles to philosophical frontiers, from infrastructure layers to governance frameworks — a rigorous deconstruction of the Metaverse\'s foundational logic',
    futureVision: 'Technology Convergence Roadmap · 10 / 20 / 30-Year Horizon',
    convergenceTitle: 'When All Technologies Converge',
    convergenceDesc: '6G, AI, Quantum Computing, Brain-Computer Interfaces, Digital Twins — as these technologies progressively converge over the next 10, 20, and 30 years, every dimension of human civilization will be fundamentally transformed.',
    qjTitle: 'Qing Jian Yuan Heng',
    qjSubtitle: 'Chief Operating Officer · Frontier Technology Visionary · Serial Cross-Domain Entrepreneur',
    coreValues: [
      { title: 'Forward Vision', desc: 'Continuously tracking the bleeding edge of global Metaverse, 6G, and Web4.0–5.0 developments to deliver the most actionable intelligence in the industry' },
      { title: 'Ecosystem Connectivity', desc: 'Aggregating 500+ core institutions across the full industry value chain, building the most influential Metaverse ecosystem network on the planet' },
      { title: 'Value Intelligence', desc: 'Delivering rigorous analysis of Metaverse investment opportunities, empowering investors with professional, data-driven decision support' },
      { title: 'Innovation Engine', desc: 'Championing Metaverse builders and developers, accelerating the deployment of next-generation digital civilization infrastructure at global scale' },
    ],
    milestones: [
      { year: '2021', event: 'META66.cn founded — establishing China\'s first dedicated Metaverse intelligence platform' },
      { year: '2022', event: 'Ecosystem partners surpassed 100, spanning VR, AR, and blockchain across the full industry chain' },
      { year: '2023', event: 'Launched the Metaverse Industry Navigator; platform users exceeded 5 million' },
      { year: '2024', event: '6G Technology Research Institute established; Web4.0 Protocol Alliance formally initiated' },
      { year: '2025', event: 'Global node network fully deployed; digital economy ecosystem activated at scale' },
      { year: '2026+', event: 'Advancing into the Web5.0 era — architecting the foundational infrastructure of digital civilization' },
    ],
  }
};

// 元宇宙底层概念 - 扩展版（9个子板块）
const META_CONCEPTS_ZH = [
  {
    id: 'definition',
    icon: 'ri-planet-line',
    color: '#00FF88',
    title: '元宇宙的本质定义',
    subtitle: '不是虚拟游戏，是平行文明',
    image: 'https://public.readdy.ai/ai/img_res/3d59b621db6d5896810a921fefce2bd1.jpg',
    content: '元宇宙（Metaverse）并非单一技术，而是由多种前沿技术融合构建的"持久化、去中心化、沉浸式"数字平行宇宙。这一概念最早由科幻作家Neal Stephenson在1992年小说《雪崩》中提出，描述了一个人类以数字化身（Avatar）生活其中的虚拟世界。\n\n元宇宙的核心特征包括：永续性（不因用户离线而消失）、实时性（与现实世界同步运行）、互操作性（跨平台资产与身份互通）、去中心化（无单一主体控制）、完整经济系统（数字价值创造与流通体系）。\n\n更深层地看，元宇宙是人类文明的第二次"大航海时代"——第一次大航海开拓了地理空间，元宇宙开拓的是意识空间与数字空间。它不是现实世界的替代品，而是现实世界的延伸与增强。',
    keyPoints: ['永续性', '实时同步', '互操作性', '去中心化', '数字经济', '意识延伸'],
    stats: [{ label: '概念提出', value: '1992年' }, { label: '市场规模', value: '30万亿$' }, { label: '活跃用户', value: '10亿+' }],
  },
  {
    id: 'logic',
    icon: 'ri-cpu-line',
    color: '#FFD700',
    title: '底层逻辑：技术三角',
    subtitle: '感知层·计算层·传输层',
    image: 'https://readdy.ai/api/search-image?query=technology%20triangle%20foundation%20XR%20AI%20quantum%20computing%206G%20network%20three%20pillars%20glowing%20golden%20dark%20background%20futuristic%20sci-fi%20concept%20art%20ultra%20detailed%208k%20yellow%20orange&width=800&height=500&seq=concept-logic-v3-001&orientation=landscape',
    content: '元宇宙的底层逻辑建立在三大技术支柱之上，形成不可分割的技术三角：\n\n① 感知层（XR技术）：VR/AR/MR/XR设备构建人机交互界面，让人类感官延伸至数字空间。裸眼3D与脑机接口将最终消除设备边界，实现"无感知"沉浸。当前XR设备已进入消费级普及阶段，Apple Vision Pro、Meta Quest 3等产品标志着感知层的成熟。\n\n② 计算层（AI+量子计算）：AI大模型驱动虚拟世界的智能生成与实时渲染，量子计算提供超越经典计算机的算力支撑，使复杂物理模拟与全球级并发成为可能。NVIDIA Omniverse平台已成为工业元宇宙的标准计算基础设施。\n\n③ 传输层（6G网络）：太赫兹频段的6G网络提供1Tbps峰值速率与亚毫秒延迟，是元宇宙实时交互的血管系统。没有6G，元宇宙的沉浸体验将永远停留在理论层面。',
    keyPoints: ['XR感知层', 'AI计算层', '6G传输层', '量子算力', '脑机接口', '数字孪生'],
    stats: [{ label: '6G峰值速率', value: '1Tbps' }, { label: 'AI算力增长', value: '10倍/年' }, { label: '延迟目标', value: '<0.1ms' }],
  },
  {
    id: 'economy',
    icon: 'ri-coin-line',
    color: '#FF6B9D',
    title: '数字经济：价值重构',
    subtitle: '虚拟资产与现实价值的融合',
    image: 'https://readdy.ai/api/search-image?query=digital%20economy%20blockchain%20NFT%20cryptocurrency%20virtual%20assets%20value%20reconstruction%20glowing%20pink%20neon%20dark%20background%20futuristic%20concept%20art%20ultra%20detailed%208k%20magenta%20purple&width=800&height=500&seq=concept-economy-v3-001&orientation=landscape',
    content: '元宇宙将重构人类的价值创造与分配体系，这是一场比工业革命更深刻的经济革命。\n\n区块链技术赋予数字资产唯一性与可验证所有权，NFT让创作者直接获得价值回报，DeFi协议构建无需中介的金融体系。在元宇宙经济中，虚拟土地、数字艺术、游戏道具、社交身份等数字资产将与实体资产同等重要。\n\n更革命性的是"创作者经济"的崛起：在元宇宙中，每个人都可以成为内容创作者、虚拟建筑师、数字艺术家。平台不再是价值的垄断者，而是价值流通的基础设施。预计到2030年，全球元宇宙经济规模将超过30万亿美元，虚拟世界的GDP将超越多数实体国家。\n\nMETA66深度布局这一价值重构进程，为投资者、创作者与开发者提供最前沿的生态连接与价值发现服务。',
    keyPoints: ['区块链确权', 'NFT创作者经济', 'DeFi金融', '虚拟土地', '数字身份', '创作者经济'],
    stats: [{ label: '2030年规模', value: '30万亿$' }, { label: 'NFT市场', value: '千亿级' }, { label: '创作者收益', value: '60%分成' }],
  },
  {
    id: 'evolution',
    icon: 'ri-git-branch-line',
    color: '#00FFCC',
    title: '演化进程：四阶段路径',
    subtitle: '从基础设施到文明形态',
    image: 'https://readdy.ai/api/search-image?query=metaverse%20evolution%20timeline%20four%20stages%20infrastructure%20content%20economy%20society%20transformation%20glowing%20cyan%20dark%20background%20futuristic%20concept%20art%20ultra%20detailed%208k%20teal%20turquoise&width=800&height=500&seq=concept-evolution-v3-001&orientation=landscape',
    content: '元宇宙的演化遵循"基础设施→内容生态→经济系统→社会形态"的四阶段路径：\n\n第一阶段（2020-2025）：基础设施建设期。6G网络商用、XR设备普及、区块链基础协议成熟，元宇宙从概念走向可用。当前我们正处于这一阶段的尾声。\n\n第二阶段（2025-2030）：内容生态爆发期。AI生成内容（AIGC）降低创作门槛，数字孪生城市全面落地，元宇宙日活用户突破10亿。这将是元宇宙的"移动互联网时刻"。\n\n第三阶段（2030-2040）：经济系统成熟期。虚实资产互通，数字身份成为法律认可的主权身份，元宇宙经济规模超越多数实体经济体。\n\n第四阶段（2040+）：社会形态重构期。脑机接口普及，虚实边界消融，人类文明进入"数字-物理融合"的新纪元。',
    keyPoints: ['基础设施期', '内容爆发期', '经济成熟期', '社会重构期', 'AIGC', '脑机融合'],
    stats: [{ label: '当前阶段', value: '第一阶段' }, { label: '2030目标', value: '10亿用户' }, { label: '2040愿景', value: '虚实融合' }],
  },
  {
    id: 'philosophy',
    icon: 'ri-mind-map',
    color: '#A855F7',
    title: '哲学维度：意识与存在',
    subtitle: '元宇宙重新定义"真实"',
    image: 'https://readdy.ai/api/search-image?query=philosophy%20consciousness%20existence%20digital%20reality%20virtual%20real%20boundary%20blur%20purple%20glowing%20mind%20universe%20dark%20background%20futuristic%20concept%20art%20ultra%20detailed%208k%20violet&width=800&height=500&seq=concept-philosophy-v3-001&orientation=landscape',
    content: '元宇宙不仅是技术问题，更是哲学问题。它迫使人类重新思考"真实"的定义。\n\n笛卡尔的"我思故我在"在元宇宙时代获得了新的诠释：当一个人在元宇宙中工作、恋爱、创作、成长，这些体验是否"真实"？当数字化身的情感体验与现实无异，虚拟与现实的边界究竟在哪里？\n\n更深层的哲学挑战来自"数字意识上传"：如果人类的意识可以被完整数字化，那么数字意识是否具有与生物意识同等的权利与尊严？这不是遥远的科幻，Neuralink已经实现了人脑与计算机的直接连接。\n\n元宇宙还将重构人类的时间观：在虚拟世界中，时间可以被加速、减速、甚至倒流。当人类可以在元宇宙中体验数百年的虚拟人生，"生命的意义"将被重新定义。META66认为，这是人类文明史上最深刻的哲学革命。',
    keyPoints: ['意识哲学', '真实性重构', '数字意识', '时间观变革', '存在主义', '数字伦理'],
    stats: [{ label: '哲学命题', value: '意识上传' }, { label: '伦理挑战', value: '数字权利' }, { label: '时间维度', value: '可编程' }],
  },
  {
    id: 'governance',
    icon: 'ri-government-line',
    color: '#FF6B35',
    title: '治理体系：数字主权',
    subtitle: '谁来统治元宇宙？',
    image: 'https://readdy.ai/api/search-image?query=digital%20governance%20sovereignty%20decentralized%20autonomous%20organization%20DAO%20blockchain%20voting%20system%20orange%20glowing%20dark%20background%20futuristic%20concept%20art%20ultra%20detailed%208k%20amber&width=800&height=500&seq=concept-governance-v3-001&orientation=landscape',
    content: '元宇宙的治理是21世纪最复杂的政治哲学问题之一。当数十亿人在虚拟世界中生活、工作、创造财富，谁有权制定规则？谁来保护用户权益？\n\n当前存在三种治理模式的博弈：\n\n① 平台中心化治理：Meta、微软等科技巨头主导，效率高但存在垄断风险，用户数据主权难以保障。\n\n② DAO去中心化自治：通过区块链智能合约实现社区自治，代币持有者参与决策，但效率低下且易被大户操控。\n\n③ 国家主权介入：各国政府将元宇宙纳入监管框架，中国的"数字人民币+元宇宙"模式、欧盟的GDPR数字权利保护，代表了不同的治理路径。\n\nMETA66认为，未来的元宇宙治理将是"多层次混合治理"：技术层由开放协议保障互操作性，经济层由DAO实现去中心化，社会层由各国法律框架保护公民权益。',
    keyPoints: ['数字主权', 'DAO治理', '平台监管', '数字法律', '用户权益', '多层治理'],
    stats: [{ label: '治理模式', value: '3种博弈' }, { label: '监管国家', value: '195个' }, { label: '核心议题', value: '数字主权' }],
  },
  // 新增3个子板块
  {
    id: 'identity',
    icon: 'ri-fingerprint-line',
    color: '#00FFCC',
    title: '数字身份：DID革命',
    subtitle: '自主主权身份',
    image: 'https://readdy.ai/api/search-image?query=digital%20identity%20DID%20decentralized%20identifier%20biometric%20authentication%20holographic%20fingerprint%20cyan%20neon%20glow%20dark%20background%20futuristic%20concept%20art%20ultra%20detailed%208k%20turquoise&width=800&height=500&seq=concept-identity-v3-001&orientation=landscape',
    content: 'DID（Decentralized Identifier，去中心化标识符）是元宇宙身份系统的基石。与传统互联网账号不同，DID让用户真正拥有自己的数字身份，无需依赖任何中心化平台。\n\n在DID系统中，用户的身份信息存储在区块链上，由用户自己控制私钥。这意味着：你可以带着自己的身份、声誉、社交关系自由迁移到任何元宇宙平台，平台无法封禁或删除你的身份。\n\n可验证凭证（Verifiable Credentials）技术让DID更加实用：学历证书、工作证明、信用记录等都可以以加密方式附加到DID上，且完全防伪造。未来，你的DID将比护照更重要，它是你在数字世界的"数字灵魂"。',
    keyPoints: ['DID标准', '自主主权', '可验证凭证', '跨平台互通', '隐私保护', '声誉系统'],
    stats: [{ label: 'DID标准', value: 'W3C认证' }, { label: '身份迁移', value: '零成本' }, { label: '隐私控制', value: '100%' }],
  },
  {
    id: 'spatial',
    icon: 'ri-map-2-line',
    color: '#FFD700',
    title: '空间计算：虚实融合',
    subtitle: '下一代人机交互',
    image: 'https://readdy.ai/api/search-image?query=spatial%20computing%20mixed%20reality%20holographic%20interface%20hand%20gesture%20control%20floating%203D%20objects%20golden%20yellow%20neon%20dark%20background%20futuristic%20concept%20art%20ultra%20detailed%208k%20amber&width=800&height=500&seq=concept-spatial-v3-001&orientation=landscape',
    content: '空间计算（Spatial Computing）是元宇宙的核心交互范式，它将数字信息无缝融入物理空间，让人类以最自然的方式与数字世界交互。\n\n不同于传统的屏幕交互，空间计算利用计算机视觉、传感器融合和AI技术，理解用户所在的三维空间环境。你可以用手势抓取虚拟物体，用眼神选择菜单，用语音下达指令——就像与真实世界互动一样自然。\n\nApple Vision Pro的发布标志着空间计算进入消费级市场。未来，空间计算将取代智能手机，成为人类与数字世界的主要接口。眼镜、隐形眼镜甚至脑机接口，都将成为空间计算的载体。',
    keyPoints: ['手势识别', '眼动追踪', '环境理解', '自然交互', '多模态融合', '无界面设计'],
    stats: [{ label: '延迟', value: '<1ms' }, { label: '精度', value: '亚毫米' }, { label: '识别率', value: '99.9%' }],
  },
  {
    id: 'aigc',
    icon: 'ri-magic-line',
    color: '#FF6B9D',
    title: 'AIGC：内容革命',
    subtitle: 'AI生成一切',
    image: 'https://readdy.ai/api/search-image?query=AIGC%20AI%20generated%20content%20creation%20neural%20network%20art%20music%20video%20text%20pink%20neon%20glow%20dark%20background%20futuristic%20concept%20art%20ultra%20detailed%208k%20magenta&width=800&height=500&seq=concept-aigc-v3-001&orientation=landscape',
    content: 'AIGC（AI Generated Content，人工智能生成内容）正在引爆元宇宙的内容生产革命。ChatGPT、Midjourney、Sora等工具证明，AI已经能够生成高质量的文本、图像、视频甚至3D模型。\n\n在元宇宙中，AIGC的价值被放大100倍：你可以用自然语言描述一个虚拟世界，AI实时生成完整的3D场景；你可以与AI NPC进行无限深度的对话，每个NPC都有独特的性格和记忆；你可以让AI为你创作专属的音乐、艺术、故事。\n\nAIGC让元宇宙的内容生产成本趋近于零，创作门槛降低到人人可为。这将带来内容生态的爆发式增长——预计2030年，元宇宙中90%的内容将由AI生成或辅助生成。',
    keyPoints: ['文本生成', '图像生成', '3D建模', 'NPC智能', '实时渲染', '个性化创作'],
    stats: [{ label: '内容成本', value: '降低99%' }, { label: '生成速度', value: '实时' }, { label: '质量水平', value: '专业级' }],
  },
];

// 英文版概念内容
const META_CONCEPTS_EN = [
  {
    id: 'definition',
    icon: 'ri-planet-line',
    color: '#00FF88',
    title: 'Essence of Metaverse',
    subtitle: 'Not a game, but a parallel civilization',
    image: 'https://readdy.ai/api/search-image?query=metaverse%20parallel%20universe%20digital%20civilization%20holographic%20world%20avatar%20virtual%20reality%20glowing%20neon%20dark%20background%20futuristic%20concept%20art%20ultra%20detailed%208k%20cinematic%20vibrant%20green%20cyan&width=800&height=500&seq=concept-definition-v3-001&orientation=landscape',
    content: 'The Metaverse is not a single technology, but a "persistent, decentralized, immersive" digital parallel universe built by integrating multiple frontier technologies. This concept was first proposed by sci-fi author Neal Stephenson in his 1992 novel "Snow Crash," describing a virtual world where humans live as digital avatars.\n\nCore characteristics include: persistence (does not disappear when users go offline), real-time synchronization, interoperability (cross-platform asset and identity exchange), decentralization (no single controlling entity), and a complete economic system.\n\nMore profoundly, the Metaverse represents humanity\'s second "Age of Exploration"—while the first explored geographical space, the Metaverse explores consciousness and digital space.',
    keyPoints: ['Persistence', 'Real-time Sync', 'Interoperability', 'Decentralization', 'Digital Economy', 'Consciousness Extension'],
    stats: [{ label: 'Concept Born', value: '1992' }, { label: 'Market Size', value: '$30T' }, { label: 'Active Users', value: '1B+' }],
  },
  {
    id: 'logic',
    icon: 'ri-cpu-line',
    color: '#FFD700',
    title: 'Tech Triangle Foundation',
    subtitle: 'Perception · Computing · Transmission',
    image: 'https://readdy.ai/api/search-image?query=technology%20triangle%20foundation%20XR%20AI%20quantum%20computing%206G%20network%20three%20pillars%20glowing%20golden%20dark%20background%20futuristic%20sci-fi%20concept%20art%20ultra%20detailed%208k%20yellow%20orange&width=800&height=500&seq=concept-logic-v3-001&orientation=landscape',
    content: 'The Metaverse\'s underlying logic rests on three technological pillars forming an inseparable triangle:\n\n① Perception Layer (XR): VR/AR/MR/XR devices build human-computer interaction interfaces. Brain-computer interfaces will eventually eliminate device boundaries.\n\n② Computing Layer (AI + Quantum): AI large models drive intelligent generation and real-time rendering. Quantum computing provides computational power beyond classical computers.\n\n③ Transmission Layer (6G): Terahertz 6G networks provide 1Tbps peak rates and sub-millisecond latency—the vascular system for real-time Metaverse interaction.',
    keyPoints: ['XR Perception', 'AI Computing', '6G Transmission', 'Quantum Power', 'BCI', 'Digital Twins'],
    stats: [{ label: '6G Peak Rate', value: '1Tbps' }, { label: 'AI Growth', value: '10x/year' }, { label: 'Latency Target', value: '<0.1ms' }],
  },
  {
    id: 'economy',
    icon: 'ri-coin-line',
    color: '#FF6B9D',
    title: 'Digital Economy Revolution',
    subtitle: 'Virtual assets meet real value',
    image: 'https://readdy.ai/api/search-image?query=digital%20economy%20blockchain%20NFT%20cryptocurrency%20virtual%20assets%20value%20reconstruction%20glowing%20pink%20neon%20dark%20background%20futuristic%20concept%20art%20ultra%20detailed%208k%20magenta%20purple&width=800&height=500&seq=concept-economy-v3-001&orientation=landscape',
    content: 'The Metaverse will reconstruct humanity\'s value creation and distribution system—a more profound economic revolution than the Industrial Revolution.\n\nBlockchain technology grants digital assets uniqueness and verifiable ownership. NFTs allow creators to directly capture value. DeFi protocols build financial systems without intermediaries.\n\nThe rise of the "Creator Economy" is revolutionary: in the Metaverse, everyone can be a content creator, virtual architect, or digital artist. By 2030, the global Metaverse economy is expected to exceed $30 trillion.',
    keyPoints: ['Blockchain Rights', 'NFT Economy', 'DeFi Finance', 'Virtual Land', 'Digital Identity', 'Creator Economy'],
    stats: [{ label: '2030 Scale', value: '$30T' }, { label: 'NFT Market', value: '$100B+' }, { label: 'Creator Share', value: '60%' }],
  },
  {
    id: 'evolution',
    icon: 'ri-git-branch-line',
    color: '#00FFCC',
    title: 'Evolution Path',
    subtitle: 'From infrastructure to civilization',
    image: 'https://readdy.ai/api/search-image?query=metaverse%20evolution%20timeline%20four%20stages%20infrastructure%20content%20economy%20society%20transformation%20glowing%20cyan%20dark%20background%20futuristic%20concept%20art%20ultra%20detailed%208k%20teal%20turquoise&width=800&height=500&seq=concept-evolution-v3-001&orientation=landscape',
    content: 'Metaverse evolution follows a four-stage path: Infrastructure → Content Ecosystem → Economic System → Social Form.\n\nPhase 1 (2020-2025): Infrastructure building. 6G commercialization, XR device普及, blockchain protocols mature.\n\nPhase 2 (2025-2030): Content explosion. AIGC lowers creation barriers, digital twin cities deploy globally.\n\nPhase 3 (2030-2040): Economic maturity. Virtual-real asset interoperability, digital identity becomes legally recognized.\n\nPhase 4 (2040+): Social reconstruction. BCI普及, boundaries blur, humanity enters a new era of digital-physical fusion.',
    keyPoints: ['Infrastructure', 'Content Boom', 'Economic Maturity', 'Social Reconstruction', 'AIGC', 'BCI Fusion'],
    stats: [{ label: 'Current Phase', value: 'Phase 1' }, { label: '2030 Target', value: '1B Users' }, { label: '2040 Vision', value: 'Full Fusion' }],
  },
  {
    id: 'philosophy',
    icon: 'ri-mind-map',
    color: '#A855F7',
    title: 'Philosophy Dimension',
    subtitle: 'Redefining "Reality"',
    image: 'https://readdy.ai/api/search-image?query=philosophy%20consciousness%20existence%20digital%20reality%20virtual%20real%20boundary%20blur%20purple%20glowing%20mind%20universe%20dark%20background%20futuristic%20concept%20art%20ultra%20detailed%208k%20violet&width=800&height=500&seq=concept-philosophy-v3-001&orientation=landscape',
    content: 'The Metaverse is not just a technological issue—it\'s a philosophical one. It forces humanity to reconsider the definition of "reality."\n\nDescartes\' "I think, therefore I am" takes on new meaning in the Metaverse era: when a person works, loves, creates, and grows in the Metaverse, are these experiences "real"?\n\nThe deeper challenge comes from "digital consciousness uploading": if human consciousness can be fully digitized, does digital consciousness have equal rights and dignity to biological consciousness?',
    keyPoints: ['Consciousness Philosophy', 'Reality Redefined', 'Digital Consciousness', 'Time Perception', 'Existentialism', 'Digital Ethics'],
    stats: [{ label: 'Key Question', value: 'Mind Upload' }, { label: 'Ethics', value: 'Digital Rights' }, { label: 'Time', value: 'Programmable' }],
  },
  {
    id: 'governance',
    icon: 'ri-government-line',
    color: '#FF6B35',
    title: 'Governance System',
    subtitle: 'Who rules the Metaverse?',
    image: 'https://readdy.ai/api/search-image?query=digital%20governance%20sovereignty%20decentralized%20autonomous%20organization%20DAO%20blockchain%20voting%20system%20orange%20glowing%20dark%20background%20futuristic%20concept%20art%20ultra%20detailed%208k%20amber&width=800&height=500&seq=concept-governance-v3-001&orientation=landscape',
    content: 'Metaverse governance is one of the 21st century\'s most complex political philosophy questions. When billions live, work, and create wealth in virtual worlds, who has the right to make rules?\n\nThree governance models compete:\n\n① Platform Centralization: Led by tech giants like Meta and Microsoft—efficient but risky, user data sovereignty hard to protect.\n\n② DAO Decentralization: Community autonomy through blockchain smart contracts—democratic but inefficient.\n\n③ State Sovereignty: Governments incorporate Metaverse into regulatory frameworks.\n\nMETA66 believes future governance will be "multi-layered hybrid": technical layer ensures interoperability, economic layer uses DAOs, social layer protects citizens through legal frameworks.',
    keyPoints: ['Digital Sovereignty', 'DAO Governance', 'Platform Regulation', 'Digital Law', 'User Rights', 'Multi-layer Governance'],
    stats: [{ label: 'Models', value: '3 Types' }, { label: 'Nations', value: '195' }, { label: 'Key Issue', value: 'Sovereignty' }],
  },
  {
    id: 'identity',
    icon: 'ri-fingerprint-line',
    color: '#00FFCC',
    title: 'Digital Identity: DID',
    subtitle: 'Self-Sovereign Identity',
    image: 'https://readdy.ai/api/search-image?query=digital%20identity%20DID%20decentralized%20identifier%20biometric%20authentication%20holographic%20fingerprint%20cyan%20neon%20glow%20dark%20background%20futuristic%20concept%20art%20ultra%20detailed%208k%20turquoise&width=800&height=500&seq=concept-identity-v3-001&orientation=landscape',
    content: 'DID (Decentralized Identifier) is the cornerstone of Metaverse identity systems. Unlike traditional internet accounts, DIDs let users truly own their digital identity without relying on any centralized platform.\n\nIn the DID system, user identity information is stored on the blockchain, controlled by the user\'s private key. This means: you can freely migrate with your identity, reputation, and social relationships to any Metaverse platform.\n\nVerifiable Credentials make DIDs more practical: academic certificates, work proofs, credit records can all be cryptographically attached to DIDs and are completely tamper-proof.',
    keyPoints: ['DID Standards', 'Self-Sovereignty', 'Verifiable Credentials', 'Cross-platform', 'Privacy Protection', 'Reputation System'],
    stats: [{ label: 'DID Standard', value: 'W3C' }, { label: 'Migration Cost', value: 'Zero' }, { label: 'Privacy Control', value: '100%' }],
  },
  {
    id: 'spatial',
    icon: 'ri-map-2-line',
    color: '#FFD700',
    title: 'Spatial Computing',
    subtitle: 'Next-gen Human-Computer Interaction',
    image: 'https://readdy.ai/api/search-image?query=spatial%20computing%20mixed%20reality%20holographic%20interface%20hand%20gesture%20control%20floating%203D%20objects%20golden%20yellow%20neon%20dark%20background%20futuristic%20concept%20art%20ultra%20detailed%208k%20amber&width=800&height=500&seq=concept-spatial-v3-001&orientation=landscape',
    content: 'Spatial Computing is the core interaction paradigm of the Metaverse, seamlessly integrating digital information into physical space for the most natural human-digital world interaction.\n\nUnlike traditional screen interactions, spatial computing uses computer vision, sensor fusion, and AI to understand the user\'s 3D spatial environment. You can grab virtual objects with gestures, select menus with eye gaze, and issue commands with voice—just like interacting with the real world.\n\nApple Vision Pro\'s release marks spatial computing entering the consumer market. In the future, spatial computing will replace smartphones as humanity\'s primary interface with the digital world.',
    keyPoints: ['Gesture Recognition', 'Eye Tracking', 'Environment Understanding', 'Natural Interaction', 'Multi-modal Fusion', 'Interface-less Design'],
    stats: [{ label: 'Latency', value: '<1ms' }, { label: 'Precision', value: 'Sub-mm' }, { label: 'Accuracy', value: '99.9%' }],
  },
  {
    id: 'aigc',
    icon: 'ri-magic-line',
    color: '#FF6B9D',
    title: 'AIGC Revolution',
    subtitle: 'AI Generates Everything',
    image: 'https://readdy.ai/api/search-image?query=AIGC%20AI%20generated%20content%20creation%20neural%20network%20art%20music%20video%20text%20pink%20neon%20glow%20dark%20background%20futuristic%20concept%20art%20ultra%20detailed%208k%20magenta&width=800&height=500&seq=concept-aigc-v3-001&orientation=landscape',
    content: 'AIGC (AI Generated Content) is igniting a content production revolution in the Metaverse. ChatGPT, Midjourney, and Sora prove that AI can already generate high-quality text, images, video, and even 3D models.\n\nIn the Metaverse, AIGC\'s value is amplified 100x: you can describe a virtual world in natural language, and AI generates complete 3D scenes in real-time; you can have unlimited-depth conversations with AI NPCs, each with unique personalities and memories; you can have AI create exclusive music, art, and stories for you.\n\nAIGC drives Metaverse content production costs toward zero, lowering creation barriers so anyone can participate.',
    keyPoints: ['Text Generation', 'Image Generation', '3D Modeling', 'NPC Intelligence', 'Real-time Rendering', 'Personalized Creation'],
    stats: [{ label: 'Content Cost', value: '-99%' }, { label: 'Generation Speed', value: 'Real-time' }, { label: 'Quality Level', value: 'Professional' }],
  },
];

/* 技术交汇未来愿景 */
const FUTURE_CONVERGENCE_ZH = [
  {
    year: '2030',
    title: '6G+AI+XR融合',
    desc: '6G网络全面商用，AI大模型实时生成虚拟世界，XR设备轻量化至普通眼镜形态。元宇宙日活用户突破10亿，虚拟办公、虚拟教育成为主流。人们开始在元宇宙中购置"第二套房产"，数字资产市值超越黄金。',
    color: '#00FF88',
    icon: 'ri-signal-tower-line',
    changes: ['虚拟办公主流化', '数字资产超越黄金', 'XR设备如眼镜普及', 'AI实时生成世界'],
  },
  {
    year: '2040',
    title: '量子+脑机+数字孪生',
    desc: '量子计算机商用化，脑机接口非侵入式普及，全球数字孪生完成。人类可以"意念上网"，思维直接与元宇宙交互。数字孪生城市实现全球覆盖，物理世界与数字世界实时同步。虚拟经济规模超越实体经济。',
    color: '#FFD700',
    icon: 'ri-brain-line',
    changes: ['意念直接上网', '虚拟经济超实体', '全球数字孪生', '脑机接口普及'],
  },
  {
    year: '2050',
    title: '意识上传+虚实融合',
    desc: '人类意识可被部分数字化，"数字永生"成为可能。虚实边界完全消融，人们在物理空间中看到的世界与元宇宙叠加显示。AI与人类协同进化，形成"人机文明"。人类开始向太阳系其他星球扩张，元宇宙成为星际文明的通信基础设施。',
    color: '#A855F7',
    icon: 'ri-rocket-line',
    changes: ['数字永生可能', '虚实完全融合', '星际元宇宙', '人机协同进化'],
  },
];

const FUTURE_CONVERGENCE_EN = [
  {
    year: '2030',
    title: '6G + AI + XR Convergence',
    desc: '6G networks reach full global commercialization. AI foundation models generate immersive virtual worlds in real time. XR devices shrink to the form factor of everyday eyewear. Metaverse daily active users surpass 1 billion. Virtual offices and digital education become the new normal. People begin acquiring "second properties" in the Metaverse, and digital asset market caps eclipse gold.',
    color: '#00FF88',
    icon: 'ri-signal-tower-line',
    changes: ['Virtual Offices Go Mainstream', 'Digital Assets Surpass Gold', 'XR Eyewear Ubiquity', 'Real-Time AI World Generation'],
  },
  {
    year: '2040',
    title: 'Quantum + BCI + Digital Twins',
    desc: 'Quantum computers enter commercial deployment. Non-invasive brain-computer interfaces achieve mass adoption. Global digital twin coverage is complete. Humans navigate the Metaverse through pure thought. Digital twin cities synchronize with the physical world in real time. The virtual economy overtakes the physical economy in total value.',
    color: '#FFD700',
    icon: 'ri-brain-line',
    changes: ['Thought-Driven Navigation', 'Virtual Economy Dominates', 'Global Digital Twin Grid', 'BCI Mass Adoption'],
  },
  {
    year: '2050',
    title: 'Consciousness Upload & Full Fusion',
    desc: 'Partial digitization of human consciousness makes "digital immortality" a tangible reality. The boundary between virtual and physical dissolves entirely — the physical world becomes a canvas overlaid with the Metaverse. AI and humanity co-evolve into a unified human-machine civilization. Interstellar expansion begins, with the Metaverse serving as the communication backbone of a multi-planetary species.',
    color: '#A855F7',
    icon: 'ri-rocket-line',
    changes: ['Digital Immortality Realized', 'Virtual-Physical Boundary Dissolved', 'Interstellar Metaverse', 'Human-AI Co-Evolution'],
  },
];

interface AboutProps {
  lang?: 'zh' | 'en';
}

export default function About({ lang = 'zh' }: AboutProps) {
  const [activeConcept, setActiveConcept] = useState('definition');
  const [activeConvergence, setActiveConvergence] = useState(0);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: conceptRef, isVisible: conceptVisible } = useScrollAnimation({ threshold: 0.04 });
  const { ref: convergenceRef, isVisible: convergenceVisible } = useScrollAnimation({ threshold: 0.04 });
  const { ref: contentRef, isVisible: contentVisible } = useScrollAnimation({ threshold: 0.04 });
  const { ref: timelineRef, isVisible: timelineVisible } = useScrollAnimation({ threshold: 0.04 });
  const { ref: qjRef, isVisible: qjVisible } = useScrollAnimation({ threshold: 0.04 });
  const { ref: statsRef, visibleItems: statsVis } = useStaggerAnimation(6, { threshold: 0.04 });

  const t = TRANSLATIONS[lang];
  const metaConcepts = lang === 'zh' ? META_CONCEPTS_ZH : META_CONCEPTS_EN;
  const futureConvergence = lang === 'zh' ? FUTURE_CONVERGENCE_ZH : FUTURE_CONVERGENCE_EN;
  const currentConcept = metaConcepts.find((c) => c.id === activeConcept)!;
  const conv = futureConvergence[activeConvergence];

  const coreValues = [
    { icon: 'ri-eye-line', title: t.coreValues[0].title, desc: t.coreValues[0].desc, color: '#00FF88' },
    { icon: 'ri-links-line', title: t.coreValues[1].title, desc: t.coreValues[1].desc, color: '#FFD700' },
    { icon: 'ri-shield-check-line', title: t.coreValues[2].title, desc: t.coreValues[2].desc, color: '#FF6B9D' },
    { icon: 'ri-rocket-line', title: t.coreValues[3].title, desc: t.coreValues[3].desc, color: '#00FFCC' },
  ];

  const milestones = t.milestones;

  return (
    <section id="metaverse" className="relative py-24 md:py-32 bg-[#020810] overflow-hidden">
      {/* Background grid */}
      <div className="absolute inset-0 pointer-events-none opacity-8" style={{
        backgroundImage: 'linear-gradient(rgba(0,255,136,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,136,0.07) 1px, transparent 1px)',
        backgroundSize: '60px 60px',
      }} />
      <div className="absolute top-20 left-10 w-64 h-64 rounded-full pointer-events-none opacity-5" style={{ background: 'radial-gradient(ellipse, rgba(255,215,0,0.5) 0%, transparent 70%)' }} />
      <div className="absolute bottom-20 right-10 w-48 h-48 rounded-full pointer-events-none opacity-5" style={{ background: 'radial-gradient(ellipse, rgba(0,255,204,0.5) 0%, transparent 70%)' }} />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">

        {/* Section Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`flex flex-col items-center text-center mb-16 md:mb-20 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div
            className="inline-flex items-center gap-2 px-5 py-2 mb-6 text-sm font-bold"
            style={{
              background: 'rgba(0,255,136,0.1)',
              border: '1.5px solid rgba(0,255,136,0.4)',
              borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
              color: '#00FF88',
              fontFamily: 'Caveat, cursive',
              fontSize: '1rem',
            }}
          >
            <i className="ri-planet-line" />
            About META66
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            {t.sectionTitle.split('数字文明')[0]}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00FF88] via-[#FFD700] to-[#FF6B9D]"> {lang === 'zh' ? '数字文明' : 'Digital Civilization'}</span>
          </h2>
          <p className="text-white/50 text-base md:text-lg max-w-3xl leading-relaxed">
            {t.sectionSubtitle}，{t.sectionDesc}
          </p>
          <div className="mt-8">
            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 hover:scale-105 whitespace-nowrap"
              style={{ background: 'rgba(0,255,136,0.12)', border: '1.5px solid rgba(0,255,136,0.4)', color: '#00FF88' }}
            >
              <i className="ri-planet-line" />
              {t.learnMore}
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>

        {/* ===== 元宇宙底层概念深度解析 - 9个子板块升级版 ===== */}
        <div
          ref={conceptRef as React.RefObject<HTMLDivElement>}
          className="mb-24"
          style={{
            opacity: conceptVisible ? 1 : 0,
            transform: conceptVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          {/* Section Title */}
          <div className="text-center mb-12">
            <div
              className="inline-flex items-center gap-3 px-6 py-2.5 mb-6"
              style={{
                background: 'linear-gradient(135deg, rgba(0,255,136,0.1), rgba(255,215,0,0.1))',
                border: '2px solid rgba(0,255,136,0.35)',
                borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                color: '#00FF88',
                fontFamily: 'Caveat, cursive',
                fontSize: '1.05rem',
              }}
            >
              <i className="ri-book-open-line" />
              {t.conceptTitle}
              <i className="ri-book-open-line" />
            </div>
            <h3 className="text-3xl md:text-4xl font-black text-white mb-4" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              {t.conceptSubtitle.split('六大维度')[0]}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00FF88] to-[#FFD700]"> {lang === 'zh' ? '六大维度' : 'Six Dimensions'}</span>
            </h3>
            <p className="text-white/40 text-base max-w-2xl mx-auto">
              {t.conceptDesc}
            </p>
          </div>

          {/* Concept Selector - 3行3列网格 */}
          <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-9 gap-2 mb-10">
            {metaConcepts.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveConcept(c.id)}
                className="group flex flex-col items-center gap-2 p-3 transition-all duration-300 cursor-pointer"
                style={{
                  background: activeConcept === c.id ? `${c.color}12` : 'rgba(255,255,255,0.02)',
                  border: `2px solid ${activeConcept === c.id ? c.color + '60' : 'rgba(255,255,255,0.08)'}`,
                  borderRadius: '14px 10px 16px 12px / 10px 16px 12px 14px',
                  transform: activeConcept === c.id ? 'translateY(-4px) scale(1.03)' : 'translateY(0) scale(1)',
                  boxShadow: activeConcept === c.id ? `0 8px 30px ${c.color}20` : 'none',
                }}
              >
                <div
                  className="w-10 h-10 flex items-center justify-center transition-all duration-300"
                  style={{
                    background: activeConcept === c.id ? `${c.color}20` : 'rgba(255,255,255,0.05)',
                    border: `1.5px solid ${activeConcept === c.id ? c.color + '50' : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                  }}
                >
                  <i className={`${c.icon} text-lg`} style={{ color: activeConcept === c.id ? c.color : 'rgba(255,255,255,0.3)' }} />
                </div>
                <p
                  className="text-xs font-bold text-center leading-tight hidden lg:block"
                  style={{
                    color: activeConcept === c.id ? c.color : 'rgba(255,255,255,0.4)',
                    fontFamily: 'Caveat, cursive',
                    fontSize: '0.75rem',
                  }}
                >
                  {c.title.split('：')[0].split(':')[0]}
                </p>
                {activeConcept === c.id && (
                  <div className="w-full h-0.5 rounded-full" style={{ background: `linear-gradient(90deg, transparent, ${c.color}, transparent)` }} />
                )}
              </button>
            ))}
          </div>

          {/* Concept Detail - Full Width */}
          <div
            key={activeConcept}
            className="grid grid-cols-1 lg:grid-cols-5 gap-8"
            style={{ animation: 'fadeInUp 0.5s ease' }}
          >
            {/* Left: Image + Stats */}
            <div className="lg:col-span-2 flex flex-col gap-4">
              <div
                className="relative rounded-2xl overflow-hidden"
                style={{
                  height: '280px',
                  border: `2px solid ${currentConcept.color}30`,
                  borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px',
                  boxShadow: `0 0 40px ${currentConcept.color}15`,
                }}
              >
                <img
                  src={currentConcept.image}
                  alt={currentConcept.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, transparent 40%, rgba(2,8,16,0.9) 100%)` }} />
                <div className="absolute bottom-4 left-4 right-4">
                  <div
                    className="flex items-center gap-2 px-3 py-2 rounded-xl bg-black/70 backdrop-blur-sm border"
                    style={{ borderColor: `${currentConcept.color}30` }}
                  >
                    <div
                      className="w-6 h-6 flex items-center justify-center"
                      style={{ background: `${currentConcept.color}20`, borderRadius: '50%' }}
                    >
                      <i className={`${currentConcept.icon} text-xs`} style={{ color: currentConcept.color }} />
                    </div>
                    <span className="text-white/80 text-xs font-semibold">{currentConcept.subtitle}</span>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                {currentConcept.stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="text-center p-3 rounded-xl"
                    style={{
                      background: `${currentConcept.color}08`,
                      border: `1px solid ${currentConcept.color}25`,
                      borderRadius: '10px 8px 12px 8px / 8px 12px 8px 10px',
                    }}
                  >
                    <p className="font-black text-base" style={{ color: currentConcept.color, fontFamily: 'Orbitron, sans-serif' }}>{stat.value}</p>
                    <p className="text-white/35 text-xs leading-tight mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* Key Points */}
              <div
                className="p-4 rounded-xl"
                style={{
                  background: `${currentConcept.color}06`,
                  border: `1px solid ${currentConcept.color}20`,
                  borderRadius: '12px 8px 14px 10px / 8px 14px 10px 12px',
                }}
              >
                <p className="text-white/40 text-xs font-bold tracking-wider mb-3">{lang === 'zh' ? '核心关键词' : 'Key Concepts'}</p>
                <div className="flex flex-wrap gap-2">
                  {currentConcept.keyPoints.map((kp) => (
                    <span
                      key={kp}
                      className="px-2.5 py-1 text-xs font-bold"
                      style={{
                        background: `${currentConcept.color}12`,
                        border: `1px solid ${currentConcept.color}35`,
                        borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                        color: currentConcept.color,
                        fontFamily: 'Caveat, cursive',
                        fontSize: '0.82rem',
                      }}
                    >
                      {kp}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Content */}
            <div className="lg:col-span-3">
              <div
                className="h-full p-7 rounded-2xl"
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px',
                }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className="w-12 h-12 flex items-center justify-center flex-shrink-0"
                    style={{
                      background: `${currentConcept.color}20`,
                      border: `2px solid ${currentConcept.color}50`,
                      borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                    }}
                  >
                    <i className={`${currentConcept.icon} text-xl`} style={{ color: currentConcept.color }} />
                  </div>
                  <div>
                    <h4 className="text-white font-black text-xl" style={{ fontFamily: 'Orbitron, sans-serif' }}>{currentConcept.title}</h4>
                    <p className="text-white/40 text-sm" style={{ fontFamily: 'Caveat, cursive' }}>{currentConcept.subtitle}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {currentConcept.content.split('\n\n').map((para, i) => (
                    <div key={i}>
                      {para.split('\n').map((line, j) => {
                        const isHighlight = line.startsWith('①') || line.startsWith('②') || line.startsWith('③') || line.startsWith('第') || line.startsWith('Phase') || line.startsWith('①');
                        return (
                          <p
                            key={j}
                            className="text-sm leading-relaxed"
                            style={{
                              color: isHighlight ? currentConcept.color : 'rgba(255,255,255,0.65)',
                              fontWeight: isHighlight ? 700 : 400,
                              marginBottom: j < para.split('\n').length - 1 ? '4px' : '0',
                              fontFamily: isHighlight ? 'Caveat, cursive' : 'inherit',
                              fontSize: isHighlight ? '0.95rem' : '0.875rem',
                            }}
                          >
                            {line}
                          </p>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== 技术交汇未来愿景 ===== */}
        <div
          ref={convergenceRef as React.RefObject<HTMLDivElement>}
          className="mb-24"
          style={{
            opacity: convergenceVisible ? 1 : 0,
            transform: convergenceVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease 0.1s, transform 0.8s ease 0.1s',
          }}
        >
          <div className="text-center mb-12">
            <div
              className="inline-flex items-center gap-3 px-6 py-2.5 mb-6"
              style={{
                background: 'linear-gradient(135deg, rgba(168,85,247,0.1), rgba(0,255,204,0.1))',
                border: '2px solid rgba(168,85,247,0.35)',
                borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                color: '#A855F7',
                fontFamily: 'Caveat, cursive',
                fontSize: '1.05rem',
              }}
            >
              <i className="ri-time-line" />
              {t.futureVision}
              <i className="ri-time-line" />
            </div>
            <h3 className="text-3xl md:text-4xl font-black text-white mb-4" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              {t.convergenceTitle.split('技术交汇')[0]}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#A855F7] to-[#00FFCC]"> {lang === 'zh' ? '技术交汇' : 'Technology Convergence'}</span>
            </h3>
            <p className="text-white/40 text-base max-w-2xl mx-auto">
              {t.convergenceDesc}
            </p>
          </div>

          {/* Year Selector */}
          <div className="flex gap-4 justify-center mb-10">
            {futureConvergence.map((fc, i) => (
              <button
                key={fc.year}
                onClick={() => setActiveConvergence(i)}
                className="flex items-center gap-3 px-6 py-3 font-bold transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={{
                  background: activeConvergence === i ? `${fc.color}15` : 'rgba(255,255,255,0.03)',
                  border: `2px solid ${activeConvergence === i ? fc.color + '60' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: '12px 8px 14px 10px / 8px 14px 10px 12px',
                  color: activeConvergence === i ? fc.color : 'rgba(255,255,255,0.4)',
                  transform: activeConvergence === i ? 'scale(1.05)' : 'scale(1)',
                  boxShadow: activeConvergence === i ? `0 0 25px ${fc.color}25` : 'none',
                  fontFamily: 'Orbitron, sans-serif',
                  fontSize: '0.9rem',
                }}
              >
                <i className={`${fc.icon} text-lg`} />
                {fc.year}{lang === 'zh' ? '年' : ''}
              </button>
            ))}
          </div>

          {/* Convergence Detail */}
          <div
            key={conv.year}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8"
            style={{ animation: 'fadeInUp 0.5s ease' }}
          >
            {/* Left: Image */}
            <div
              className="relative rounded-2xl overflow-hidden"
              style={{
                height: '360px',
                border: `2px solid ${conv.color}30`,
                borderRadius: '20px 14px 22px 16px / 14px 22px 16px 20px',
                boxShadow: `0 0 50px ${conv.color}15`,
              }}
            >
              <img
                src={`https://readdy.ai/api/search-image?query=${conv.year === '2030' ? 'futuristic city 2030 6G network AI holographic displays XR glasses people working virtual office glowing neon dark background ultra detailed 8k cinematic' : conv.year === '2040' ? 'futuristic 2040 brain computer interface quantum computing digital twin city consciousness upload glowing golden dark background ultra detailed 8k cinematic' : 'futuristic 2050 consciousness upload virtual reality fusion physical digital world space colonization purple glowing dark background ultra detailed 8k cinematic'}&width=800&height=500&seq=convergence-${conv.year}-v3-001&orientation=landscape`}
                alt={conv.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, transparent 30%, rgba(2,8,16,0.85) 100%)` }} />
              <div className="absolute top-4 left-4">
                <div
                  className="px-4 py-2 font-black text-2xl"
                  style={{
                    background: `${conv.color}20`,
                    border: `2px solid ${conv.color}50`,
                    borderRadius: '10px 8px 12px 8px / 8px 12px 8px 10px',
                    color: conv.color,
                    fontFamily: 'Orbitron, sans-serif',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  {conv.year}
                </div>
              </div>
              <div className="absolute bottom-4 left-4 right-4">
                <h4 className="text-white font-black text-xl mb-1" style={{ fontFamily: 'Orbitron, sans-serif' }}>{conv.title}</h4>
                <div className="flex flex-wrap gap-2">
                  {conv.changes.map((ch) => (
                    <span
                      key={ch}
                      className="px-2.5 py-1 text-xs font-bold"
                      style={{
                        background: `${conv.color}20`,
                        border: `1px solid ${conv.color}40`,
                        borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                        color: conv.color,
                        fontFamily: 'Caveat, cursive',
                        backdropFilter: 'blur(8px)',
                      }}
                    >
                      {ch}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Content */}
            <div className="flex flex-col justify-center gap-6">
              <div
                className="p-6 rounded-2xl"
                style={{
                  background: `${conv.color}06`,
                  border: `1.5px solid ${conv.color}25`,
                  borderRadius: '18px 14px 20px 16px / 14px 20px 16px 18px',
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-12 h-12 flex items-center justify-center"
                    style={{
                      background: `${conv.color}20`,
                      border: `2px solid ${conv.color}50`,
                      borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                    }}
                  >
                    <i className={`${conv.icon} text-xl`} style={{ color: conv.color }} />
                  </div>
                  <div>
                    <p className="text-white font-black text-lg" style={{ fontFamily: 'Orbitron, sans-serif' }}>{conv.year}{lang === 'zh' ? '年愿景' : ' Vision'}</p>
                    <p style={{ color: conv.color }} className="text-sm font-semibold">{conv.title}</p>
                  </div>
                </div>
                <p className="text-white/65 text-sm leading-relaxed" style={{ fontFamily: 'Caveat, cursive', fontSize: '0.95rem', lineHeight: '1.8' }}>
                  {conv.desc}
                </p>
              </div>

              {/* Tech Convergence Visual */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { tech: lang === 'zh' ? '6G网络' : '6G Network', role: lang === 'zh' ? '传输血管' : 'Transmission', icon: 'ri-signal-tower-line' },
                  { tech: lang === 'zh' ? 'AI大模型' : 'AI Models', role: lang === 'zh' ? '智能大脑' : 'Intelligence', icon: 'ri-brain-line' },
                  { tech: lang === 'zh' ? '量子计算' : 'Quantum', role: lang === 'zh' ? '算力引擎' : 'Computing', icon: 'ri-bubble-chart-line' },
                  { tech: lang === 'zh' ? '脑机接口' : 'BCI', role: lang === 'zh' ? '感知接口' : 'Interface', icon: 'ri-mental-health-line' },
                ].map((item) => (
                  <div
                    key={item.tech}
                    className="flex items-center gap-3 p-3 rounded-xl"
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '10px 8px 12px 8px / 8px 12px 8px 10px',
                    }}
                  >
                    <div
                      className="w-8 h-8 flex items-center justify-center flex-shrink-0"
                      style={{
                        background: `${conv.color}15`,
                        border: `1px solid ${conv.color}35`,
                        borderRadius: '50% 40% 60% 45% / 45% 60% 40% 55%',
                      }}
                    >
                      <i className={`${item.icon} text-sm`} style={{ color: conv.color }} />
                    </div>
                    <div>
                      <p className="text-white/70 text-xs font-bold">{item.tech}</p>
                      <p className="text-white/30 text-xs">{item.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ===== 清鉴元衡先生介绍 ===== */}
        <div
          ref={qjRef as React.RefObject<HTMLDivElement>}
          className="mb-24"
          style={{
            opacity: qjVisible ? 1 : 0,
            transform: qjVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease 0.1s, transform 0.8s ease 0.1s',
          }}
        >
          <div
            className="relative rounded-3xl overflow-hidden p-8 md:p-12"
            style={{
              background: 'linear-gradient(135deg, rgba(0,255,136,0.06), rgba(255,215,0,0.06), rgba(0,255,204,0.04))',
              border: '2px solid rgba(0,255,136,0.2)',
              borderRadius: '28px 20px 32px 24px / 20px 32px 24px 28px',
            }}
          >
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 pointer-events-none opacity-5" style={{ background: 'radial-gradient(ellipse, rgba(0,255,136,0.8) 0%, transparent 70%)' }} />
            <div className="absolute bottom-0 left-0 w-48 h-48 pointer-events-none opacity-5" style={{ background: 'radial-gradient(ellipse, rgba(255,215,0,0.8) 0%, transparent 70%)' }} />

            <div className="relative z-10 flex flex-col lg:flex-row gap-10 items-start">
              {/* Avatar */}
              <div className="flex-shrink-0">
                <div
                  className="relative w-36 h-36 overflow-hidden"
                  style={{
                    border: '3px solid rgba(0,255,136,0.5)',
                    borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                    boxShadow: '0 0 40px rgba(0,255,136,0.2)',
                  }}
                >
                  <img
                    src="https://readdy.ai/api/search-image?query=professional%20asian%20man%20COO%20technology%20executive%20portrait%20confident%20intelligent%20dark%20background%20studio%20lighting%20suit&width=300&height=300&seq=qjyh-avatar-001&orientation=squarish"
                    alt="清鉴元衡"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 60%, rgba(0,255,136,0.15) 100%)' }} />
                </div>
                {/* Contact Card */}
                <div
                  className="mt-4 p-4 text-center"
                  style={{
                    background: 'rgba(0,255,136,0.06)',
                    border: '1px solid rgba(0,255,136,0.25)',
                    borderRadius: '12px 8px 14px 10px / 8px 14px 10px 12px',
                  }}
                >
                  <p className="text-[#00FF88] text-xs font-bold mb-1" style={{ fontFamily: 'Caveat, cursive' }}>{lang === 'zh' ? '微信/电话同号' : 'WeChat/Phone'}</p>
                  <p className="text-white font-black text-base" style={{ fontFamily: 'Orbitron, sans-serif' }}>138-1000-1256</p>
                  <p className="text-white/40 text-xs mt-1">{lang === 'zh' ? '期待与您沟通交流' : 'Looking forward to connecting'}</p>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-bold"
                    style={{
                      background: 'rgba(0,255,136,0.12)',
                      border: '1.5px solid rgba(0,255,136,0.4)',
                      borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                      color: '#00FF88',
                      fontFamily: 'Caveat, cursive',
                    }}
                  >
                    <i className="ri-user-star-line" />
                    META66 COO
                  </div>
                </div>

                <h3 className="text-white font-black text-3xl md:text-4xl mb-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  {t.qjTitle.split('清鉴')[0]}
                  <span className="text-[#00FF88]">{lang === 'zh' ? '元衡' : ' Yuan Heng'}</span>
                </h3>
                <p className="text-white/50 text-sm mb-6" style={{ fontFamily: 'Caveat, cursive', fontSize: '1rem' }}>
                  {t.qjSubtitle}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                  <div>
                    <p className="text-[#00FF88] text-xs font-bold mb-2 tracking-widest uppercase" style={{ fontFamily: 'Caveat, cursive' }}>{lang === 'zh' ? '职业背景' : 'Background'}</p>
                    <p className="text-white/65 text-sm leading-relaxed">
                      {lang === 'zh' 
                        ? '清鉴元衡先生现任某科技公司COO，职业生涯横跨金融、科技、传媒、文化创意等多个领域，每次跨行均能迅速登顶，展现出超凡的学习能力与战略眼光。他深信"跨界是最好的创新"，将不同行业的底层逻辑融会贯通，形成了独特的商业洞察力。'
                        : 'Mr. Qing Jian Yuan Heng currently serves as COO of a technology company. His career spans finance, technology, media, and cultural creativity. He believes "cross-domain is the best innovation," integrating underlying logic from different industries to form unique business insights.'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#FFD700] text-xs font-bold mb-2 tracking-widest uppercase" style={{ fontFamily: 'Caveat, cursive' }}>{lang === 'zh' ? '前沿科技热情' : 'Tech Passion'}</p>
                    <p className="text-white/65 text-sm leading-relaxed">
                      {lang === 'zh'
                        ? '对元宇宙、AI、脑机接口、量子计算等前沿科技有着近乎痴迷的热情。他不仅是技术的观察者，更是实践者——亲身参与多个元宇宙项目的战略规划与落地执行，将前沿科技与商业价值深度融合。'
                        : 'He has an almost obsessive passion for frontier technologies like Metaverse, AI, brain-computer interfaces, and quantum computing. He is not just an observer but a practitioner—personally participating in strategic planning and execution of multiple Metaverse projects.'}
                    </p>
                  </div>
                </div>

                <div
                  className="p-5 rounded-xl mb-6"
                  style={{
                    background: 'rgba(255,215,0,0.06)',
                    border: '1px solid rgba(255,215,0,0.2)',
                    borderRadius: '12px 8px 14px 10px / 8px 14px 10px 12px',
                  }}
                >
                  <p className="text-[#FFD700] text-xs font-bold mb-2" style={{ fontFamily: 'Caveat, cursive' }}>{lang === 'zh' ? '人生哲学' : 'Philosophy'}</p>
                  <p className="text-white/70 text-sm leading-relaxed italic" style={{ fontFamily: 'Caveat, cursive', fontSize: '0.95rem' }}>
                    "{lang === 'zh' 
                      ? '每一次跨行，都是一次对自我边界的突破。真正的高手，不是在一个领域做到极致，而是能在任何领域迅速找到核心规律，并将其转化为竞争优势。元宇宙是我见过的最大的跨界机会——它将重构所有行业的底层逻辑。'
                      : 'Every career transition is a breakthrough of self-boundaries. True masters are not those who excel in one field, but those who can quickly identify core patterns in any field and transform them into competitive advantages. The Metaverse is the greatest cross-domain opportunity I have ever seen.'}"
                  </p>
                </div>

                <div className="flex flex-wrap gap-3">
                  {(lang === 'zh' 
                    ? ['元宇宙战略', '跨界创新', '前沿科技', 'AI应用', '数字经济', '商业洞察']
                    : ['Metaverse Strategy', 'Cross-domain Innovation', 'Frontier Tech', 'AI Applications', 'Digital Economy', 'Business Insights']
                  ).map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 text-xs font-bold"
                      style={{
                        background: 'rgba(0,255,136,0.08)',
                        border: '1px solid rgba(0,255,136,0.25)',
                        borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                        color: 'rgba(0,255,136,0.8)',
                        fontFamily: 'Caveat, cursive',
                        fontSize: '0.82rem',
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div
          ref={contentRef as React.RefObject<HTMLDivElement>}
          className={`flex flex-col lg:flex-row gap-12 lg:gap-16 mb-20 transition-all duration-700 delay-200 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {/* Left - Image */}
          <div className="flex-1 relative">
            <div className="relative h-[400px] md:h-[480px]">
              <div className="absolute inset-0 rounded-2xl overflow-hidden border border-[#00FF88]/20" style={{ boxShadow: '0 0 40px rgba(0,255,136,0.08)' }}>
                <img
                  src="https://readdy.ai/api/search-image?query=futuristic%20metaverse%20virtual%20city%20holographic%20buildings%20glowing%20colorful%20neon%20lights%20digital%20world%20immersive%20environment%20sci-fi%20cyberpunk%20dark%20background%20cinematic%20doodle%20art%20style%20vibrant%20colors&width=700&height=500&seq=about-main-doodle-001&orientation=landscape"
                  alt={lang === 'zh' ? '元宇宙虚拟城市' : 'Metaverse Virtual City'}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#020810]/80 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-black/60 backdrop-blur-sm border border-[#00FF88]/20">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#00FF88]/20">
                      <i className="ri-building-4-line text-[#00FF88] text-sm" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">META66 {lang === 'zh' ? '虚拟总部' : 'Virtual HQ'}</p>
                      <p className="text-white/40 text-xs">{lang === 'zh' ? '元宇宙数字空间 · 全球在线' : 'Metaverse Digital Space · Global Online'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="absolute -top-4 -right-4 w-48 rounded-xl bg-black/80 backdrop-blur-sm border border-[#00FF88]/30 p-4" style={{ boxShadow: '0 0 20px rgba(0,255,136,0.12)', borderRadius: '14px 10px 14px 10px / 10px 14px 10px 14px' }}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-2 h-2 rounded-full bg-[#00FF88] animate-pulse" />
                  <span className="text-[#00FF88] text-xs font-semibold" style={{ fontFamily: 'Caveat, cursive' }}>{lang === 'zh' ? '实时数据' : 'Live Data'}</span>
                </div>
                <p className="text-white text-2xl font-black" style={{ fontFamily: 'Orbitron, sans-serif' }}>3,200<span className="text-[#00FF88]">万+</span></p>
                <p className="text-white/40 text-xs mt-1">{lang === 'zh' ? '全球活跃用户' : 'Global Active Users'}</p>
              </div>

              <div className="absolute -bottom-4 -left-4 w-44 rounded-xl bg-black/80 backdrop-blur-sm border border-[#FFD700]/30 p-4" style={{ boxShadow: '0 0 20px rgba(255,215,0,0.1)', borderRadius: '10px 14px 10px 14px / 14px 10px 14px 10px' }}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-2 h-2 rounded-full bg-[#FFD700] animate-pulse" />
                  <span className="text-[#FFD700] text-xs font-semibold" style={{ fontFamily: 'Caveat, cursive' }}>{lang === 'zh' ? '生态伙伴' : 'Partners'}</span>
                </div>
                <p className="text-white text-2xl font-black" style={{ fontFamily: 'Orbitron, sans-serif' }}>500<span className="text-[#FFD700]">+</span></p>
                <p className="text-white/40 text-xs mt-1">{lang === 'zh' ? '全球合作机构' : 'Global Partners'}</p>
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div className="flex-1 flex flex-col justify-center gap-8">
            <div className="flex flex-col gap-4">
              <h3 className="text-2xl md:text-3xl font-bold text-white">
                {lang === 'zh' ? '元宇宙时代的' : 'The '}<span className="text-[#00FF88]">{lang === 'zh' ? '核心门户' : 'Core Portal'}</span>{lang === 'zh' ? '' : ' of the Metaverse Era'}
              </h3>
              <p className="text-white/50 leading-relaxed text-sm">
                {lang === 'zh'
                  ? '在数字文明加速演进的今天，META66.cn 以前瞻性的战略眼光，深度布局元宇宙全产业链。我们不仅是资讯平台，更是连接技术创新者、投资者与产业生态的核心枢纽。'
                  : 'In today\'s accelerating digital civilization evolution, META66.cn deeply layouts the entire Metaverse industry chain with forward-looking strategic vision. We are not just an information platform, but a core hub connecting technology innovators, investors, and industrial ecosystems.'}
              </p>
              <p className="text-white/50 leading-relaxed text-sm">
                {lang === 'zh'
                  ? '随着6G网络的商用落地，Web4.0去中心化协议的成熟，以及XR设备的普及，元宇宙正从概念走向现实。META66.cn 将持续引领这场数字文明的伟大变革，为每一位参与者提供最权威的知识、最前沿的洞察与最广泛的生态连接。'
                  : 'With the commercial deployment of 6G networks, the maturation of Web4.0 decentralized protocols, and the普及 of XR devices, the Metaverse is moving from concept to reality. META66.cn will continue to lead this great transformation of digital civilization.'}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {coreValues.map((v) => (
                <div key={v.title} className="flex gap-3 p-4 rounded-xl bg-white/3 border border-white/8 hover:border-white/20 transition-all duration-300 group">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 transition-colors duration-300" style={{ backgroundColor: `${v.color}12` }}>
                    <i className={`${v.icon} text-lg`} style={{ color: v.color }} />
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold mb-1">{v.title}</p>
                    <p className="text-white/40 text-xs leading-relaxed">{v.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div
          ref={timelineRef as React.RefObject<HTMLDivElement>}
          className={`border-t border-white/8 pt-16 transition-all duration-700 delay-300 ${timelineVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <h3 className="text-2xl font-bold text-white mb-10 text-center">
            {lang === 'zh' ? '发展' : 'Development '}<span className="text-[#00FF88]">{lang === 'zh' ? '历程' : 'History'}</span>
          </h3>
          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#00FF88]/60 via-[#00FF88]/20 to-transparent hidden md:block" />
            <div className="flex flex-col gap-6">
              {milestones.map((m, i) => {
                const colors = ['#00FF88', '#FFD700', '#FF6B9D', '#00FFCC', '#00FF88', '#FFD700'];
                return (
                  <div key={m.year} className={`flex flex-col md:flex-row items-center gap-4 md:gap-8 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <div className={`inline-flex flex-col gap-1 p-4 rounded-xl bg-white/3 border border-white/8 hover:border-white/20 transition-all duration-300 ${i % 2 === 0 ? 'md:items-end' : 'md:items-start'}`}>
                        <span className="text-sm font-bold" style={{ fontFamily: 'Orbitron, sans-serif', color: colors[i] }}>{m.year}</span>
                        <span className="text-white/70 text-sm">{m.event}</span>
                      </div>
                    </div>
                    <div
                      className="hidden md:flex w-4 h-4 flex-shrink-0 z-10 rounded-full"
                      style={{ backgroundColor: colors[i], boxShadow: `0 0 12px ${colors[i]}80`, border: `3px solid #020810` }}
                    />
                    <div className="flex-1 hidden md:block" />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}